import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  X, MessageSquare, SkipForward, Star, Flame, Calendar,
  TrendingUp, ChevronDown, ChevronUp
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_COLORS = {
  spark: { bg: 'bg-amber-100', text: 'text-amber-700', dot: 'bg-amber-400' },
  depth: { bg: 'bg-orange-100', text: 'text-orange-700', dot: 'bg-orange-500' },
  breakthrough: { bg: 'bg-purple-100', text: 'text-purple-700', dot: 'bg-purple-500' },
};

const ScoreDot = ({ score }) => {
  if (!score) return <span className="text-charcoal/25 text-xs">—</span>;
  const color = score >= 8 ? '#22c55e' : score >= 5 ? '#ff780a' : '#ef4444';
  return (
    <span className="inline-flex items-center gap-1 text-xs font-medium" style={{ color }}>
      <Star size={10} fill={color} />{score}/10
    </span>
  );
};

const StatCard = ({ label, value, sub, icon: Icon, accent }) => (
  <div className="p-4 rounded-2xl bg-white/60 border border-charcoal/5 flex flex-col gap-1">
    <div className="flex items-center gap-1.5 mb-1">
      {Icon && <Icon size={14} className="text-charcoal/40" />}
      <span className="text-charcoal/50 text-xs uppercase tracking-wider">{label}</span>
    </div>
    <span className="font-serif text-2xl text-charcoal" style={accent ? { color: accent } : {}}>
      {value ?? '—'}
    </span>
    {sub && <span className="text-xs text-charcoal/40">{sub}</span>}
  </div>
);

const MiniBar = ({ label, value, max, color }) => (
  <div className="flex items-center gap-2 text-xs">
    <span className="text-charcoal/50 w-24 truncate capitalize">{label}</span>
    <div className="flex-1 h-1.5 bg-charcoal/10 rounded-full overflow-hidden">
      <motion.div
        initial={{ width: 0 }}
        animate={{ width: `${max > 0 ? (value / max) * 100 : 0}%` }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="h-full rounded-full"
        style={{ backgroundColor: color || '#ff780a' }}
      />
    </div>
    <span className="text-charcoal/60 w-6 text-right">{value}</span>
  </div>
);

const LEVEL_PALETTE = { spark: '#f59e0b', depth: '#ff780a', breakthrough: '#7c3aed' };
const TYPE_PALETTE = ['#ff780a', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899'];

const ProfileModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [diary, setDiary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null); // thought id to expand
  const [showAllDiscussions, setShowAllDiscussions] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      setLoading(true);
      axios.get(`${API}/user/diary`)
        .then(r => setDiary(r.data))
        .catch(() => setDiary(null))
        .finally(() => setLoading(false));
    }
  }, [isOpen, user]);

  if (!isOpen) return null;

  const stats = diary?.stats;
  const discussions = diary?.discussions || [];
  const visibleDiscussions = showAllDiscussions ? discussions : discussions.slice(0, 12);

  const maxLevel = stats ? Math.max(...Object.values(stats.by_level || {}), 1) : 1;
  const maxType = stats ? Math.max(...Object.values(stats.by_type || {}), 1) : 1;

  const typeKeys = Object.entries(stats?.by_type || {}).sort((a, b) => b[1] - a[1]);
  const levelOrder = ['spark', 'depth', 'breakthrough'];
  const levelKeys = levelOrder.filter(l => (stats?.by_level || {})[l]);

  const levelTitle = user?.level_title || 'Curious';
  const totalPoints = user?.total_points || 0;
  const streak = user?.streak_days || 0;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center"
        style={{ backgroundColor: 'rgba(44, 36, 23, 0.45)', backdropFilter: 'blur(4px)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 40 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
          className="w-full sm:max-w-2xl max-h-[92vh] rounded-t-3xl sm:rounded-3xl overflow-hidden flex flex-col"
          style={{ backgroundColor: '#FFF8F0' }}
          onClick={e => e.stopPropagation()}
          data-testid="profile-modal"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-6 pb-4 flex-shrink-0">
            <div>
              <h2 className="font-serif text-2xl text-charcoal">
                {user?.name || user?.email?.split('@')[0] || 'Your diary'}
              </h2>
              <p className="text-charcoal/50 text-sm mt-0.5">{user?.email}</p>
            </div>
            <button onClick={onClose}
              className="p-2 rounded-full hover:bg-charcoal/5 transition-colors"
              data-testid="close-profile-modal">
              <X size={20} className="text-charcoal/60" />
            </button>
          </div>

          {/* Scrollable body */}
          <div className="overflow-y-auto flex-1 px-6 pb-8 space-y-6">

            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="w-10 h-10 rounded-full border-2 border-orange-primary/30 border-t-orange-primary animate-spin" />
              </div>
            ) : (
              <>
                {/* ── Top stats ── */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <StatCard label="Conversations" value={stats?.total_discussed ?? 0}
                    icon={MessageSquare} accent="#ff780a" />
                  <StatCard label="Skipped" value={stats?.total_skipped ?? 0}
                    icon={SkipForward} />
                  <StatCard label="Days Active" value={stats?.days_active ?? 0}
                    icon={Calendar} />
                  <StatCard label="Avg Score"
                    value={stats?.avg_score != null ? `${stats.avg_score}/10` : '—'}
                    icon={Star} accent={stats?.avg_score >= 7 ? '#22c55e' : '#ff780a'} />
                </div>

                {/* ── Level badge + points ── */}
                <div className="flex items-center gap-3 p-4 rounded-2xl bg-orange-primary/5 border border-orange-primary/15">
                  <div className="w-10 h-10 rounded-full bg-orange-primary/10 flex items-center justify-center flex-shrink-0">
                    <Flame size={18} className="text-orange-primary" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-baseline gap-2">
                      <span className="font-medium text-charcoal">{levelTitle}</span>
                      <span className="text-charcoal/40 text-xs">Level {user?.level || 1}</span>
                    </div>
                    <div className="text-charcoal/50 text-xs">{totalPoints} points
                      {streak > 0 && <span className="ml-2 text-orange-primary">{streak} day streak</span>}
                    </div>
                  </div>
                </div>

                {/* ── By Depth ── */}
                {levelKeys.length > 0 && (
                  <div>
                    <h3 className="text-charcoal/50 text-xs uppercase tracking-wider mb-3">By Depth</h3>
                    <div className="space-y-2.5">
                      {levelKeys.map(l => (
                        <MiniBar key={l} label={l} value={stats.by_level[l]}
                          max={maxLevel} color={LEVEL_PALETTE[l]} />
                      ))}
                    </div>
                  </div>
                )}

                {/* ── By Type ── */}
                {typeKeys.length > 0 && (
                  <div>
                    <h3 className="text-charcoal/50 text-xs uppercase tracking-wider mb-3">By Type</h3>
                    <div className="space-y-2.5">
                      {typeKeys.map(([t, v], i) => (
                        <MiniBar key={t} label={t} value={v}
                          max={maxType} color={TYPE_PALETTE[i % TYPE_PALETTE.length]} />
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Conversation history ── */}
                <div>
                  <h3 className="text-charcoal/50 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                    <TrendingUp size={12} /> Culture Diary
                  </h3>

                  {discussions.length === 0 ? (
                    <div className="text-center py-10 text-charcoal/40 text-sm">
                      <MessageSquare size={32} className="mx-auto mb-3 opacity-30" />
                      No conversations yet — start with a thought on the home page.
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {visibleDiscussions.map((d, i) => {
                        const colors = LEVEL_COLORS[d.level] || LEVEL_COLORS.spark;
                        const date = d.completed_at
                          ? new Date(d.completed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
                          : '';
                        const isExpanded = expanded === d.id;
                        return (
                          <motion.div
                            key={d.id}
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: i * 0.03 }}
                            className="rounded-xl border border-charcoal/5 bg-white/50 overflow-hidden"
                            data-testid={`diary-entry-${d.id}`}
                          >
                            <button
                              className="w-full text-left p-3 flex items-start gap-3"
                              onClick={() => setExpanded(isExpanded ? null : d.id)}
                            >
                              <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${colors.dot}`} />
                              <div className="flex-1 min-w-0">
                                <p className={`text-charcoal text-sm leading-snug ${isExpanded ? '' : 'line-clamp-2'}`}>
                                  {d.content}
                                </p>
                                <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                  <span className={`text-xs px-1.5 py-0.5 rounded ${colors.bg} ${colors.text}`}>
                                    {d.level}
                                  </span>
                                  <span className="text-xs text-charcoal/30 capitalize">{d.type}</span>
                                  <span className="text-xs text-charcoal/30">{date}</span>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <ScoreDot score={d.score} />
                                {isExpanded
                                  ? <ChevronUp size={14} className="text-charcoal/30" />
                                  : <ChevronDown size={14} className="text-charcoal/30" />}
                              </div>
                            </button>
                          </motion.div>
                        );
                      })}

                      {discussions.length > 12 && (
                        <button
                          onClick={() => setShowAllDiscussions(v => !v)}
                          className="w-full py-2 text-sm text-charcoal/50 hover:text-charcoal transition-colors"
                        >
                          {showAllDiscussions
                            ? 'Show less'
                            : `Show all ${discussions.length} conversations`}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default ProfileModal;
